import { WorkflowHiringReadinessScoreWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { z } from 'zod';
import { WorkflowPromotionReadinessScoreWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/promotion-readiness-score-write-back/promotion-readiness-score-write-back';
import { WorkflowHiringNotificationsDetails } from 'libs/end-to-end/src/types/databases/workflows/hiring-notifications/workflow-hiring-notifications';
import { WorkflowCandidateResumeScoreWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/candidate-resume-score-write-back/workflow-candidate-resume-score-write-back';
import { WorkflowAIInterviewerV2WriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/ai-interviewer-v2-write-back/workflow-ai-interviewer-v2-write-back';
import { WorkflowHiringReadinessExplanationWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-explanation-write-back/workflow-hiring-readiness-explanation-write-back';
import { WorkflowPromotionReadinessExplanationWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/promotion-readiness-explanation-write-back/workflow-promotion-readiness-explanation-write-back';
import { WorkflowApplicantStageChangeUpdateDetails } from 'libs/end-to-end/src/types/databases/workflows/applicant-stage-change-update/workflow-applicant-stage-change-update';
import { WorkflowBadLanguageDetectorWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/bad-language-detector-write-back/workflow-bad-language-detector-write-back';
import { DatabaseScheduleRetryableEnableFor } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-retryable-enable-for';

export const DatabaseScheduleConfigurationType = z.enum([
  'Debug (Manual)',
  'Debug (Every 5 min)', // deprecate this.
  'Every 3 Hours',
  'Hourly',
  'Every 8 Hours',
  'Every 12 Hours',
  'Daily',
  'Weekly',
  'Bi-Weekly',
  'Monthly',
]);

export const DatabaseScheduleConfigurationTypes = Object.values(
  DatabaseScheduleConfigurationType.Values,
);

export type DatabaseScheduleConfigurationType = z.infer<
  typeof DatabaseScheduleConfigurationType
>;

export const DatabaseScheduleStatus = z.enum([
  'Healthy',
  'NeedsAttention',
  'InRetryableState',
]);

export type DatabaseScheduleStatus = z.infer<typeof DatabaseScheduleStatus>;

export const DatabaseScheduleStatuses = Object.values(
  DatabaseScheduleStatus.Values,
);

export const DatabaseSchedulePullOrUpdateOverrideConfigTypeValue = z.enum([
  'pull-latest',
  'disable-pull',
  'take-latest',
]);

export type DatabaseSchedulePullOrUpdateOverrideConfigTypeValue = z.infer<
  typeof DatabaseSchedulePullOrUpdateOverrideConfigTypeValue
>;

export const DatabaseSchedulePullOrUpdateOverrideConfigType = z.object({
  type: DatabaseSchedulePullOrUpdateOverrideConfigTypeValue,
});

export type DatabaseSchedulePullOrUpdateOverrideConfigType = z.infer<
  typeof DatabaseSchedulePullOrUpdateOverrideConfigType
>;

export const DatabaseSchedulePullOrUpdateOverrideDataSetConfig = z.object({
  dataSetId: z.string(),
  configType: DatabaseSchedulePullOrUpdateOverrideConfigType,
});

export type DatabaseSchedulePullOrUpdateOverrideDataSetConfig = z.infer<
  typeof DatabaseSchedulePullOrUpdateOverrideDataSetConfig
>;

export const ForceRunPromotionCopilotMode = z.enum([
  'none',
  'scores',
  'scores-and-explanations',
]);
export type ForceRunPromotionCopilotMode = z.infer<
  typeof ForceRunPromotionCopilotMode
>;

export const ForceRunHiringCopilotMode = z.enum([
  'scores',
  'scores-and-explanations',
]);
export type ForceRunHiringCopilotMode = z.infer<
  typeof ForceRunHiringCopilotMode
>;

export const DatabaseScheduleTargetType = z.enum(['dataset', 'transform']);

export type DatabaseScheduleTargetType = z.infer<
  typeof DatabaseScheduleTargetType
>;

export const DatabaseScheduleTargetDataSet = z.object({
  type: z.intersection(z.literal('dataset'), DatabaseScheduleTargetType),
  dataSetId: z.string(),
});

export type DatabaseScheduleTargetDataSet = z.infer<
  typeof DatabaseScheduleTargetDataSet
>;

export const DatabaseScheduleTargetTransform = z.object({
  type: z.intersection(z.literal('transform'), DatabaseScheduleTargetType),
});

export type DatabaseScheduleTargetTransform = z.infer<
  typeof DatabaseScheduleTargetTransform
>;

export const DatabaseScheduleTarget = z.union([
  DatabaseScheduleTargetDataSet,
  DatabaseScheduleTargetTransform,
]);

export type DatabaseScheduleTarget = z.infer<typeof DatabaseScheduleTarget>;

export const DatabaseScheduleRetryableDisabledConfig = z.object({
  type: z.literal('disabled'),
});

export type DatabaseScheduleRetryableDisabledConfig = z.infer<
  typeof DatabaseScheduleRetryableDisabledConfig
>;

export const DatabaseScheduleRetryInterval = z.object({
  value: z.number(),
  unit: z.enum(['minutes', 'hours', 'days']),
});

export type DatabaseScheduleRetryInterval = z.infer<
  typeof DatabaseScheduleRetryInterval
>;

export const DatabaseScheduleRetryableEnabledConfig = z.object({
  type: z.literal('enabled'),
  maxRetryAttempts: z.number(),
  retryInterval: DatabaseScheduleRetryInterval,
  enableFor: z.array(DatabaseScheduleRetryableEnableFor),
  notifyOnFailure: z.boolean(),
});

export type DatabaseScheduleRetryableEnabledConfig = z.infer<
  typeof DatabaseScheduleRetryableEnabledConfig
>;

export const DatabaseScheduleRetryableConfig = z.union([
  DatabaseScheduleRetryableDisabledConfig,
  DatabaseScheduleRetryableEnabledConfig,
]);

export type DatabaseScheduleRetryableConfig = z.infer<
  typeof DatabaseScheduleRetryableConfig
>;

export const DatabaseScheduleEditableFields = z.object({
  name: z.string().default('N/A'),
  scheduleConfigurationType: DatabaseScheduleConfigurationType,
  shouldDisableProducePullIterations: z.boolean().optional(),
  status: DatabaseScheduleStatus,
  hiringScoreWriteBackConfig:
    WorkflowHiringReadinessScoreWriteBackDetails.optional(),
  hiringExplanationWriteBackConfig:
    WorkflowHiringReadinessExplanationWriteBackDetails.optional(),
  candidateResumeScoreWriteBackConfig:
    WorkflowCandidateResumeScoreWriteBackDetails.optional(),
  badLanguageDetectorWriteBackConfig:
    WorkflowBadLanguageDetectorWriteBackDetails.optional(),
  promotionScoreWriteBackConfig:
    WorkflowPromotionReadinessScoreWriteBackDetails.optional(),
  promotionExplanationWriteBackConfig:
    WorkflowPromotionReadinessExplanationWriteBackDetails.optional(),
  hiringNotificationsConfig: WorkflowHiringNotificationsDetails.optional(),
  aiInterviewerWriteBackConfig:
    WorkflowAIInterviewerV2WriteBackDetails.optional(),
  applicantStageChangeUpdateConfig:
    WorkflowApplicantStageChangeUpdateDetails.optional(),
  pullOrUpdateOverrideDataSetConfigs: z
    .array(DatabaseSchedulePullOrUpdateOverrideDataSetConfig)
    .default([]),
  forceRunPromotionCopilot: ForceRunPromotionCopilotMode.optional(),
  forceRunHiringCopilot: ForceRunHiringCopilotMode.optional(),
  enableDagBasedDataSync: z.boolean().default(false),
  skipTopLevelVerification: z.boolean().default(false),
  skipCandidateRevisionIndex: z.boolean().default(false),
  target: DatabaseScheduleTarget.default({
    type: 'transform',
  }),
  retryableConfig: DatabaseScheduleRetryableConfig.default({
    type: 'disabled',
  }),
});

export type DatabaseScheduleEditableFields = z.infer<
  typeof DatabaseScheduleEditableFields
>;

const DatabaseSchedulePatchableFields = z.object({
  details: z.object({
    nextScheduledEpoch: z.number().nullable(),
    amountOfRetriesAttempted: z.number().default(0),
  }),
});

const DatabaseScheduleEditableAndPatchableFields =
  DatabaseScheduleEditableFields.merge(DatabaseSchedulePatchableFields);

const DatabaseSchedulePrivateFields = z.object({
  organizationId: z.string(),
  databaseId: z.string(),
  scheduleId: z.string(),
});

export const DatabaseSchedule =
  DatabaseScheduleEditableAndPatchableFields.merge(
    DatabaseSchedulePrivateFields,
  );

export type DatabaseSchedule = z.infer<typeof DatabaseSchedule>;

export const DatabaseScheduleForRead = z.object({
  schedule: DatabaseSchedule.nullable(),
});

export type DatabaseScheduleForRead = z.infer<typeof DatabaseScheduleForRead>;

export const DatabaseSchedulesForRead = z.object({
  schedules: z.array(DatabaseSchedule),
});

export const DatabaseScheduleForReadWithOrgAndDbInfo = DatabaseSchedule.extend({
  database: z
    .object({
      id: z.string(),
      name: z.string(),
      organizationId: z.string(),
      description: z.string(),
    })
    .nullable(),
  organization: z
    .object({
      id: z.string(),
      name: z.string(),
      logoMimeType: z.any().nullable(),
      logoUrl: z.any().nullable(),
    })
    .nullable(),
});

export const DatabaseSchedulesForReadWithOrgAndDbInfo = z.object({
  schedules: z.array(DatabaseScheduleForReadWithOrgAndDbInfo),
});

export type DatabaseScheduleForReadWithOrgAndDbInfo = z.infer<
  typeof DatabaseScheduleForReadWithOrgAndDbInfo
>;

export type DatabaseSchedulesForReadWithOrgAndDbInfo = z.infer<
  typeof DatabaseSchedulesForReadWithOrgAndDbInfo
>;

export type DatabaseSchedulesForRead = z.infer<typeof DatabaseSchedulesForRead>;

export const DatabaseScheduleForPatch =
  DatabaseScheduleEditableAndPatchableFields.partial();

export type DatabaseScheduleForPatch = z.infer<typeof DatabaseScheduleForPatch>;

export const DatabaseScheduleForUpdate = z.object({
  data: z.union([
    DatabaseScheduleEditableFields.extend({
      type: z.literal('configure'),
    }),
    z.object({
      type: z.literal('remove'),
    }),
  ]),
});

export type DatabaseScheduleForUpdate = z.infer<
  typeof DatabaseScheduleForUpdate
>;
